import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ReactiveFormsModule, FormGroup, FormControl } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { RouterModule } from '@angular/router';
import { MessageHandlerService } from '@core/services/message-handler/message-handler.service';
import { Store } from '@ngrx/store';
import { InputModule } from '@sharedModule/components/input/input.module';
import { sortDirection } from '@sharedModule/constants/data-grid.constants';
import { ATTRIBUTES_CONSTANT } from '@sharedModule/constants/fields.constant';
import { HasAccessDirective } from '@sharedModule/directives/has-access.directive';
import { MaterialModule } from '@sharedModule/material/material.module';
import { ConfirmationCommonDialogData } from '@sharedModule/models/confirmation-dialog.model';
import { ConfirmationDialogService } from '@sharedModule/services/confirmation-dialog/confirmation-dialog.service';
import { getActiveSort } from '@sharedModule/utils/sort.utils';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { ROLE_TYPE } from '../../constant/upsert-role.constant';
import { RoleService } from '../../services/role.service';
import {
  updateRoleListFilter,
  deleteRole,
} from '../../store/role-list.actions';
import { selectRoleList } from '../../store/role-list.selector';
import { RoleListInterface } from '../../types/role-list';
import {
  defaultRoleListFilter,
  RoleListFilter,
} from '../../types/role-list-filter';

@Component({
  selector: 'app-role-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MaterialModule,
    ReactiveFormsModule,
    HasAccessDirective,
    InputModule,
  ],
  templateUrl: './role-list.component.html',
  styleUrl: './role-list.component.scss',
})
export class RoleListComponent implements OnInit, OnDestroy {
  public readonly attrConstant = ATTRIBUTES_CONSTANT.roles;
  public readonly roleType = ROLE_TYPE;
  filterForm!: FormGroup;
  displayedColumns: string[] = ['name', 'role_type', 'description', 'action'];
  dataSource!: MatTableDataSource<RoleListInterface>;
  activeSort?: Sort;
  fallBackSort = {
    active: 'created_at',
    direction: sortDirection.desc,
  };
  page = 0;
  total = 0;
  limit = 10;
  subject = new Subject<{
    role_type: string;
  }>();
  #destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private roleService: RoleService,
    private messageHandlerService: MessageHandlerService,
    private confirmationDialogService: ConfirmationDialogService
  ) {}

  ngOnInit(): void {
    this.filterForm = new FormGroup({
      [this.attrConstant.roleType.key]: new FormControl(''),
    });
    this.loadData();
    this.dispatchFilter();
    const debounceTimeDuration = 1000;
    this.subject
      .pipe(debounceTime(debounceTimeDuration), takeUntil(this.#destroy$))
      .subscribe(res => {
        this.dispatchFilter({
          count: 10,
          page: 0,
          ...res,
        });
      });
  }

  loadData(): void {
    this.store
      .select(selectRoleList)
      .pipe(takeUntil(this.#destroy$))
      .subscribe(data => {
        if (data.rawData) {
          this.dataSource = new MatTableDataSource(data.rawData);
          this.page = data.page;
          this.total = data.totalRecords;
          this.limit = data.count;
        }
      });
  }

  applyFilter(): void {
    this.subject.next(this.filterForm.value);
  }

  clearFilters(): void {
    this.filterForm.reset();
    this.dispatchFilter({ ...defaultRoleListFilter });
  }

  onPageChange(pageEvent: PageEvent): void {
    this.dispatchFilter({
      count: pageEvent.pageSize,
      page: pageEvent.pageIndex,
    });
  }

  onSortChange(sortEvent: Sort): void {
    this.activeSort = getActiveSort(sortEvent, this.fallBackSort);
    this.dispatchFilter({
      sort_key: this.activeSort.active,
      sort_by: this.activeSort.direction,
    });
  }

  dispatchFilter(roleListFilter?: RoleListFilter): void {
    this.store.dispatch(updateRoleListFilter({ filter: roleListFilter ?? {} }));
  }

  async onDeleteRole(roleId: string): Promise<void> {
    const alertCommonDialogData: ConfirmationCommonDialogData = {
      title: '',
      message: `Are you sure you're going to delete this role?`,
      submitButtonText: 'Confirm',
    };
    const isConfirmed =
      await this.confirmationDialogService.openConfirmationDialog(
        alertCommonDialogData
      );
    if (isConfirmed) {
      this.roleService
        .deleteRole({ role_id: roleId })
        .pipe(takeUntil(this.#destroy$))
        .subscribe({
          next: x => {
            if (x.success) {
              this.messageHandlerService.show(x.message);
              this.store.dispatch(deleteRole({ role_id: roleId }));
            }
          },
        });
    }
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
